// ============================================================
// src/hooks/useDbFavorites.ts
// Changements :
// Structure Firestore : { userId, type, itemId, createdAt }
// ============================================================
 
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
 
export type FavoriteType = 'deal' | 'partner' | 'consultant';
 
export function useDbFavorites(type: FavoriteType) {
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
 
  // Charger les favoris depuis Firestore
  const fetchFavorites = useCallback(async () => {
    if (!user) { setFavoriteIds(new Set()); setLoading(false); return; }
 
    try {
      const snap = await getDocs(
        query(
          collection(db, 'favorites'),
          where('userId', '==', user.id),
          where('type', '==', type)
        )
      );
      const ids = new Set<string>();
      snap.docs.forEach(d => { if (d.data().itemId) ids.add(d.data().itemId); });
      setFavoriteIds(ids);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [user, type]);
 
  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);
 
  // Basculer un favori
  const toggleFavorite = async (itemId: string) => {
    if (!user) {
      toast({ title: 'Connexion requise', description: 'Connectez-vous pour ajouter des favoris', variant: 'destructive' });
      return;
    }
 
    const isFav = favoriteIds.has(itemId);
 
    // Mise à jour optimiste
    setFavoriteIds(prev => {
      const next = new Set(prev);
      isFav ? next.delete(itemId) : next.add(itemId);
      return next;
    });
 
    try {
      if (isFav) {
        // Trouver et supprimer le document
        const snap = await getDocs(
          query(
            collection(db, 'favorites'),
            where('userId', '==', user.id),
            where('type', '==', type),
            where('itemId', '==', itemId)
          )
        );
        await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
        toast({ title: 'Retiré des favoris' });
      } else {
        // Créer le favori
        await addDoc(collection(db, 'favorites'), {
          userId: user.id,
          type,
          itemId,
          createdAt: Timestamp.now(),
        });
        toast({ title: 'Ajouté aux favoris' });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Annuler la mise à jour optimiste
      setFavoriteIds(prev => {
        const next = new Set(prev);
        isFav ? next.add(itemId) : next.delete(itemId);
        return next;
      });
      toast({ title: 'Erreur', description: 'Impossible de modifier les favoris', variant: 'destructive' });
    }
  };
 
  const isFavorite = (itemId: string) => favoriteIds.has(itemId);
 
  return { favoriteIds, toggleFavorite, isFavorite, loading, refetch: fetchFavorites };
}
 